import type { ClientToServerEvents, ServerToClientEvents } from '@discordo/shared';
import { io, type Socket } from 'socket.io-client';

/**
 * Socket.io connection to the realtime gateway.
 *
 * The generics are the shared event contract, so a wrong event name or payload
 * is a build error on both ends instead of a frame nobody listens to.
 *
 * There is no module-level connection on purpose. A connection is a resource
 * with a lifetime, and the only component that needs one — the app shell —
 * owns it: it opens one when it mounts and disconnects it when it unmounts.
 * A shared singleton torn down by one mount and still referenced by the next
 * is a socket that looks connected and silently delivers nothing.
 *
 * The session travels in the httpOnly cookie the handshake carries, which is
 * why `withCredentials` matters even on the same origin.
 */

export type DiscordoSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export function createSocket(): DiscordoSocket {
  return io({
    withCredentials: true,
    // Reconnecting is the whole reason Socket.io is here: a laptop that
    // sleeps, a wifi handover or a backend restart must heal on their own.
    // Every reconnection redoes the handshake, so a session that expired
    // meanwhile is refused instead of silently resurrected.
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5_000,
  });
}
